import React, { useState, useEffect } from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";
import { ReactComponent as TrashIcon } from "../../../../assets/icons/icon-trash.svg";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type ScheduleEntry = {
    id: string;
    day: string;
    start: string;
    end: string;
};

export type AdScheduleSuggestion = {
    campaign_id: string;
    campaign_name: string;
    before_schedules: ScheduleEntry[];
    suggested_schedules: ScheduleEntry[];
    reason?: string;
};

type Props = {
    schedules: AdScheduleSuggestion[];
    formData: Record<string, ScheduleEntry[]>; // campaign_id -> array of schedules
    onChange: (id: string, schedules: ScheduleEntry[]) => void;
};

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------
const DAYS = ["月曜日", "火曜日", "水曜日", "木曜日", "金曜日", "土曜日", "日曜日", "月〜金", "土日", "月〜日"];
const TIMES = Array.from({ length: 97 }, (_, i) => {
    const h = Math.floor(i / 4).toString().padStart(2, "0");
    const m = ((i % 4) * 15).toString().padStart(2, "0");
    return i === 96 ? "24:00" : `${h}:${m}`;
});

// -----------------------------------------------------------------------------
// Component: AdScheduleForm
// -----------------------------------------------------------------------------
const AdScheduleForm: React.FC<Props> = ({ schedules, formData, onChange }) => {
    // Local state for the "Add New" inputs per campaign
    const [newScheduleData, setNewScheduleData] = useState<Record<string, { day: string; start: string; end: string }>>({});

    // Initialize the default selected day/time for each campaign if not set
    useEffect(() => {
        const initialSetup: Record<string, { day: string; start: string; end: string }> = {};
        schedules.forEach(cp => {
            if (!newScheduleData[cp.campaign_id]) {
                initialSetup[cp.campaign_id] = { day: "月〜日", start: "09:00", end: "18:00" };
            }
        });
        if (Object.keys(initialSetup).length > 0) {
            setNewScheduleData(prev => ({ ...prev, ...initialSetup }));
        }
    }, [schedules]);

    const handleAddSchedule = (campaignId: string, currentList: ScheduleEntry[]) => {
        const newEntry = newScheduleData[campaignId];
        if (!newEntry) return;

        const generatedId = `sch_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const updatedList = [...currentList, { id: generatedId, ...newEntry }];
        onChange(campaignId, updatedList);
    };

    const handleRemoveSchedule = (campaignId: string, currentList: ScheduleEntry[], removeId: string) => {
        const updatedList = currentList.filter(item => item.id !== removeId);
        onChange(campaignId, updatedList);
    };

    const handleNewScheduleChange = (campaignId: string, field: "day" | "start" | "end", value: string) => {
        setNewScheduleData(prev => ({
            ...prev,
            [campaignId]: {
                ...(prev[campaignId] || { day: "月〜日", start: "09:00", end: "18:00" }),
                [field]: value
            }
        }));
    };

    return (
        <div className={styles.adScheduleFormContainer}>
            {schedules.map((cp) => {
                const currentListRaw = formData[cp.campaign_id] || cp.suggested_schedules;
                const currentList = Array.isArray(currentListRaw) ? currentListRaw : [];
                const beforeList = Array.isArray(cp.before_schedules) ? cp.before_schedules : [];
                const newEntry = newScheduleData[cp.campaign_id] || { day: "月〜日", start: "09:00", end: "18:00" };

                return (
                    <section key={cp.campaign_id} className={styles.campaignCard}>
                        <div className={styles.campaignName}>{cp.campaign_name}</div>

                        {cp.reason && (
                            <div className={styles.reasonBar}>
                                <strong>Aibex提案理由:</strong> {cp.reason}
                            </div>
                        )}

                        <div className={styles.suggestionBox}>
                            {/* Before - List of Current Schedules */}
                            <div className={styles.beforePanel}>
                                <div className={styles.panelTitle}>現在の設定</div>
                                <div className={styles.locationList}>
                                    {beforeList.length === 0 && (
                                        <div className={styles.emptyText}>スケジュール設定なし</div>
                                    )}
                                    {beforeList.map(sch => (
                                        <div key={sch.id} className={styles.locationItemBefore}>
                                            <span className={styles.locName}>{sch.day} {sch.start} - {sch.end}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Arrow */}
                            <div className={styles.arrowIcon}>→</div>

                            {/* After - Editable List of Suggested Schedules */}
                            <div className={styles.afterPanel}>
                                <div className={styles.panelTitle}>AI提案</div>

                                {/* "Add new" row */}
                                <div className={styles.locationSearchRow}>
                                    <div className={styles.scheduleSelectGroup}>
                                        <select
                                            value={newEntry.day}
                                            onChange={(e) => handleNewScheduleChange(cp.campaign_id, "day", e.target.value)}
                                        >
                                            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <select
                                            value={newEntry.start}
                                            onChange={(e) => handleNewScheduleChange(cp.campaign_id, "start", e.target.value)}
                                        >
                                            {TIMES.map(t => <option key={`start-${t}`} value={t}>{t}</option>)}
                                        </select>
                                        <span>〜</span>
                                        <select
                                            value={newEntry.end}
                                            onChange={(e) => handleNewScheduleChange(cp.campaign_id, "end", e.target.value)}
                                        >
                                            {TIMES.map(t => <option key={`end-${t}`} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className={styles.addButtons}>
                                        <button
                                            type="button"
                                            className={`${styles.addBtn} ${styles.targetBtn}`}
                                            onClick={() => handleAddSchedule(cp.campaign_id, currentList)}
                                        >
                                            追加
                                        </button>
                                    </div>
                                </div>

                                {/* List of Active Schedules */}
                                <div className={styles.locationListEditable}>
                                    {currentList.length === 0 && (
                                        <div className={styles.emptyText}>スケジュール設定なし</div>
                                    )}
                                    {currentList.map(sch => (
                                        <div key={sch.id} className={styles.locationItemAfter}>
                                            <div className={styles.locItemMain}>
                                                <span className={styles.locName}>{sch.day} {sch.start} - {sch.end}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.removeBtn}
                                                onClick={() => handleRemoveSchedule(cp.campaign_id, currentList, sch.id)}
                                                title="削除"
                                            >
                                                <TrashIcon width={16} height={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AdScheduleForm;
